
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from './components/ContactForm';

export default function ContactPage() {
  useEffect(() => {
    document.title = 'Contact SparkBoost Labs | Sacramento AI Revenue Systems | Free Assessment';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contact SparkBoost Labs for your free revenue assessment. Sacramento office at 1401 21st Street. Get predictable growth systems for your service business.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1B365D] to-[#2BB6A3] py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl font-bold mb-6">
                Ready to Build Predictable Revenue?
              </h1>
              <p className="text-xl mb-8 opacity-90">
                Get your free revenue assessment and discover how AI-powered systems can 
                transform your service business into a predictable growth engine.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-16">
                {/* Contact Form */}
                <div>
                  <h2 className="text-3xl font-bold text-[#1B365D] mb-6">Get Your Free Assessment</h2>
                  <p className="text-gray-600 mb-8">
                    Schedule a complimentary 30-minute revenue assessment where we'll analyze your 
                    current systems and identify opportunities for predictable growth.
                  </p>
                  <ContactForm />
                </div>

                {/* Contact Information */}
                <div>
                  <h2 className="text-3xl font-bold text-[#1B365D] mb-6">Contact Information</h2>
                  
                  <div className="space-y-8">
                    {/* Office Location */}
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#2BB6A3] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B365D] mb-2">Office Location</h3>
                        <p className="text-gray-600">
                          9542 Bevon Pl<br />
                          Tujunga, CA 91042<br />
                          United States
                        </p>
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#2BB6A3] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B365D] mb-2">Phone</h3>
                        <p className="text-gray-600">(310) 650-2551</p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#2BB6A3] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B365D] mb-2">Email</h3>
                        <p className="text-gray-600">info@sparkboostlabs.com</p>
                      </div>
                    </div>

                    {/* Business Hours */}
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#2BB6A3] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1B365D] mb-2">Business Hours</h3>
                        <div className="text-gray-600 space-y-1">
                          <p>Monday - Friday: 8:00 AM - 6:00 PM PST</p>
                          <p>Saturday: 9:00 AM - 2:00 PM PST</p>
                          <p>Sunday: Closed</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Map */}
                  <div className="mt-12">
                    <h3 className="font-semibold text-[#1B365D] mb-4">Find Us</h3>
                    <div className="bg-gray-100 rounded-lg overflow-hidden">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3302.5!2d-118.2437!3d34.2522!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c2c75ddc27da13%3A0x24fe618a761d4738!2s9542%20Bevon%20Pl%2C%20Tujunga%2C%20CA%2091042!5e0!3m2!1sen!2sus!4v1234567890123"
                        width="100%"
                        height="300"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        title="SparkBoost Labs Office Location"
                      ></iframe>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-[#1B365D] mb-16 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-8">
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-xl font-bold text-[#1B365D] mb-3">
                    How long does it take to see results?
                  </h3>
                  <p className="text-gray-600">
                    Most clients see initial results within 30-60 days, with significant improvements 
                    typically achieved within 90 days. Our systems are designed for both quick wins 
                    and long-term sustainable growth.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-xl font-bold text-[#1B365D] mb-3">
                    What types of businesses do you work with?
                  </h3>
                  <p className="text-gray-600">
                    We specialize in service-based businesses including consultants, agencies, legal 
                    firms, accounting practices, and other professional service companies that want 
                    to scale beyond founder-led sales.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-xl font-bold text-[#1B365D] mb-3">
                    Do you work with companies outside Sacramento?
                  </h3>
                  <p className="text-gray-600">
                    Yes! While we're based in Sacramento, we work with service-based companies 
                    throughout California and across the United States. Most of our work is done 
                    remotely with regular video consultations.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-8 shadow-lg">
                  <h3 className="text-xl font-bold text-[#1B365D] mb-3">
                    What's included in the free assessment?
                  </h3>
                  <p className="text-gray-600">
                    Our free assessment includes a 30-minute consultation where we analyze your 
                    current revenue systems, identify bottlenecks and opportunities, and provide 
                    a customized roadmap for implementing predictable revenue systems.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
